import { Signal, type ProceduralBeat } from "@engine";
import type { ScoreSystem } from "../scoring/ScoreSystem";
import type { Player } from "../player/Player";
import type { HUD } from "../ui/HUD";
import type { Sparks } from "./Sparks";
import type { MusicDirector } from "../audio/MusicDirector";

// Combo at which Frenzy automatically triggers. Tuned so that a
// disciplined player gets it reliably ~40-60s into a clean run.
const TRIGGER_COMBO = 12;
// How long Frenzy stays active. Long enough to feel cinematic, short
// enough that the chaos doesn't overstay its welcome.
const DURATION_S = 7;
// Cooldown after a Frenzy ends before another one can fire. Without
// it a sustained 12+ combo would re-trigger immediately on every
// segment clear -- this caps it to ~one Frenzy per 30s.
const COOLDOWN_S = 25;

const SCORE_MUL = 2;
const SPEED_MUL = 1.25;
const BLOOM_BOOST = 0.55;

// Frenzy mode: a high-combo "go nuts for a few seconds" payoff.
//   * Score multiplier doubles segment gains.
//   * World speed scales 1.25x (via streamer.setSpeedMul on the game
//     side -- we just expose the number).
//   * Bloom boost layered on top of audio-EMA so the screen visibly
//     hot-cuts.
//   * Music director gets an intensity boost so lead/wobble/stabs
//     punch above their normal level.
//   * HUD shows a pulsing "FRENZY!" banner.
//   * Sparks burst at the player position + beat crash on activation.
//
// Activation is gated by combo crossing TRIGGER_COMBO AND the cooldown
// from the previous Frenzy having expired. Death (player.onDied) and
// any state reset (clear()) end Frenzy immediately.
export class FrenzyMode {
  // Public signal so StatsService can bump the lifetime/per-run
  // frenzy counter exactly once per activation.
  readonly onActivated = new Signal<void>();

  private score: ScoreSystem;
  private player: Player;
  private hud: HUD;
  private sparks: Sparks;
  private beat: ProceduralBeat;
  private music: MusicDirector;

  private active = false;
  private remainingSec = 0;
  private cooldownSec = 0;
  private unsubs: Array<() => void> = [];

  constructor(
    score: ScoreSystem,
    player: Player,
    hud: HUD,
    sparks: Sparks,
    beat: ProceduralBeat,
    music: MusicDirector
  ) {
    this.score = score;
    this.player = player;
    this.hud = hud;
    this.sparks = sparks;
    this.beat = beat;
    this.music = music;

    this.unsubs.push(
      this.score.onComboChanged.on((c) => this.tryActivate(c))
    );
    this.unsubs.push(this.player.onDied.on(() => this.deactivate(true)));
  }

  // Drains the active timer and the cooldown. Driven on UNSCALED dt so
  // a slow-mo pickup doesn't extend Frenzy duration in real time.
  update(unscaledDt: number): void {
    if (this.active) {
      this.remainingSec -= unscaledDt;
      if (this.remainingSec <= 0) this.deactivate(false);
    } else if (this.cooldownSec > 0) {
      this.cooldownSec = Math.max(0, this.cooldownSec - unscaledDt);
    }
  }

  // Snapped to 1 when off so MorphHexGame can multiply unconditionally.
  scoreMul(): number {
    return this.active ? SCORE_MUL : 1;
  }

  speedMul(): number {
    return this.active ? SPEED_MUL : 1;
  }

  bloomBoost(): number {
    if (!this.active) return 0;
    // Tiny ramp at the very start + the very end so the bloom doesn't
    // hard-cut on/off. Linear in the unscaled-time domain.
    const t = this.remainingSec;
    if (t > DURATION_S - 0.25) {
      const k = (DURATION_S - t) / 0.25;
      return BLOOM_BOOST * k;
    }
    if (t < 0.4) {
      return BLOOM_BOOST * Math.max(0, t / 0.4);
    }
    return BLOOM_BOOST;
  }

  isActive(): boolean {
    return this.active;
  }

  // External reset hook (showMenu / startGame / advanceLevel) so a
  // fresh run never carries leftover Frenzy state.
  clear(): void {
    if (this.active) this.deactivate(true);
    this.cooldownSec = 0;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
  }

  private tryActivate(combo: number): void {
    if (this.active) return;
    if (this.cooldownSec > 0) return;
    if (combo < TRIGGER_COMBO) return;
    this.activate();
  }

  private activate(): void {
    this.active = true;
    this.remainingSec = DURATION_S;
    this.cooldownSec = 0;

    this.hud.setFrenzy(true);
    this.hud.spawnPopup("FRENZY!", "hot");
    this.hud.flash("gate", "#ffd23f");

    // Visual + audio bang on activation. triggerCrash() with no time
    // argument defers to the next beat boundary inside ProceduralBeat,
    // which is close enough for a "BANG, FRENZY!" feel.
    this.sparks.burstAt(this.player.mesh.position, 0xffd23f, 32);
    this.beat.triggerCrash(undefined, 0.55);

    // Push the music director into "always-drop" intensity for the
    // whole Frenzy duration. MusicDirector handles the ramp + decay.
    this.music.setIntensityBoost(1.25, DURATION_S);

    this.onActivated.emit();
  }

  private deactivate(silent: boolean): void {
    if (!this.active && !silent) return;
    this.active = false;
    this.remainingSec = 0;
    this.cooldownSec = COOLDOWN_S;
    this.hud.setFrenzy(false);
    if (!silent) {
      this.hud.spawnPopup("COOLDOWN", "warm");
    }
  }
}
